/**
 * Líder usando Web Locks API
 * Garantiza que solo una pestaña maneje el timeout global
 */

import { idleConfig } from '../config';
import { getBus } from '../bus';
import { getActivityDetector } from '../activity';
import { LogoutCallback } from '../types';
import { MESSAGE_TYPES, LOGOUT_REASONS } from '../constants';

export class WebLocksLeader {
  private isRunning = false;
  private abortController: AbortController | null = null;
  private timeoutId: number | null = null;
  private unsubscribeBus: (() => void) | null = null;
  private logout: LogoutCallback;
  private countdownInterval: ReturnType<typeof setInterval> | null = null;
  private logoutScheduledAt: number | null = null;

  constructor(logout: LogoutCallback) {
    this.logout = logout;
  }

  async start(): Promise<void> {
    if (this.isRunning) {
      return;
    }

    this.isRunning = true;
    this.abortController = new AbortController();

    try {
      // Suscribirse a mensajes de actividad
      this.setupActivityListener();

      // No usar await aquí porque runAsLeader nunca termina
      navigator.locks.request(
        'igl-leader',
        { mode: 'exclusive', signal: this.abortController.signal },
        this.runAsLeader
      ).catch(error => {
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }
      });
    } catch (error) {
      console.error('Error starting WebLocks leader', { error }, 'WebLocksLeader');
      throw error;
    }
  }

  stop(): void {
    if (!this.isRunning) {
      return;
    }

    this.isRunning = false;

    // Cancelar el lock
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    // Limpiar timeout
    this.clearTimeout();

    // Limpiar countdown
    this.clearCountdown();

    // Desuscribirse del bus
    if (this.unsubscribeBus) {
      this.unsubscribeBus();
      this.unsubscribeBus = null;
    }
  }

  private setupActivityListener() {
    const bus = getBus();
    this.unsubscribeBus = bus.subscribe((message) => {

      if (message.type === MESSAGE_TYPES.ACTIVITY && 'ts' in message) {
        this.onActivityReceived(message.ts);
      }
    });
  }

  private runAsLeader = async (): Promise<void> => {
    if (!this.isRunning) return;

    // Programar el primer timeout
    this.scheduleTimeout();

    // Mantener el lock hasta que se cancele
    return new Promise((resolve) => {
      const checkAbort = () => {
        if (this.abortController?.signal.aborted) {
          resolve();
        } else {
          setTimeout(checkAbort, 100);
        }
      };
      checkAbort();
    });
  };

  private onActivityReceived(activityTimestamp: number) {
    if (!this.isRunning) {
      return;
    }

    // Reprogramar el timeout con la nueva actividad
    this.scheduleTimeout(activityTimestamp);
  }

  private scheduleTimeout(lastActivity?: number) {
    // Limpiar timeout anterior
    this.clearTimeout();

    if (!this.isRunning) return;

    // Obtener la última actividad
    const activityDetector = getActivityDetector();
    const lastActivityTime = lastActivity || activityDetector.getLastActivity();

    // Calcular tiempo restante
    const now = Date.now();
    const elapsed = now - lastActivityTime;
    const remaining = idleConfig.idleMs - elapsed;

    if (remaining <= 0) {
      // Ya expiró, ejecutar logout inmediatamente
      this.executeLogout();
      return;
    }

    // Programar timeout para el tiempo restante
    this.timeoutId = window.setTimeout(() => {
      this.executeLogout();
    }, remaining);

    // Iniciar cuenta atrás visual
    this.startCountdown(remaining);
  }

  private clearTimeout() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  private clearCountdown() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
      this.logoutScheduledAt = null;
    }
  }

  private startCountdown(remainingMs: number) {
    this.clearCountdown();

    this.logoutScheduledAt = Date.now() + remainingMs;

    // Countdown cada segundo
    this.countdownInterval = setInterval(() => {
      if (!this.logoutScheduledAt || !this.isRunning) {
        this.clearCountdown();
        return;
      }

      const now = Date.now();
      const remaining = this.logoutScheduledAt - now;

      if (remaining <= 0) {
        this.clearCountdown();
        return;
      }
    }, 1000);
  }

  private executeLogout() {
    if (!this.isRunning) return;

    // Notificar a otras pestañas
    try {
      const bus = getBus();
      bus.publish({
        type: MESSAGE_TYPES.LOGOUT,
        reason: LOGOUT_REASONS.IDLE_TIMEOUT,
      });
    } catch (error) {
      console.error('Error notifying logout to other tabs', { error }, 'WebLocksLeader');
    }

    // Ejecutar logout local
    try {
      this.logout(LOGOUT_REASONS.IDLE_TIMEOUT);
    } catch (error) {
      console.error('Error executing logout callback', { error }, 'WebLocksLeader');
    }
  }

  // Método estático para verificar soporte
  static isSupported(): boolean {
    return typeof navigator !== 'undefined' && 
           typeof navigator.locks !== 'undefined' &&
           typeof navigator.locks.request === 'function';
  }
}
